import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { AuthState } from './auth';
import { AppShell } from './AppShell';
import { LoginScreen } from '@/features/auth/LoginScreen';
import { HomeTab } from '@/features/home/HomeTab';
import { NotFound } from './NotFound';
import { RouteError } from './RouteError';
import { UpdateToast } from '@/features/pwa/UpdateToast';
import { PaidToast } from '@/features/order/PaidToast';
import { pendingLegalQueryOptions } from '@/entities/legal/queries';
import { isGateExempt, LEGAL_ACCEPT_PATH, needsAcceptance } from '@/features/legal/gate';

/**
 * Everything below the first screen loads as its own chunk.
 *
 * What stays eager is what the very first paint needs: the login screen and the home tab. A
 * person arrives at one of those two and nothing else, so pulling the car form's 91-make catalog
 * and every profile subscreen into that same download only delays it.
 *
 * The failure mode this creates is handled, not ignored: see `chunkReload.ts`. A split bundle
 * without that handling turns a routine deploy into «the screen does not open» for every tab
 * that was already open.
 */
const RegisterScreen = lazyRouteComponent(
  () => import('@/features/auth/RegisterScreen'),
  'RegisterScreen',
);
const ForgotPasswordScreen = lazyRouteComponent(
  () => import('@/features/auth/ForgotPasswordScreen'),
  'ForgotPasswordScreen',
);
const ResetPasswordScreen = lazyRouteComponent(
  () => import('@/features/auth/ResetPasswordScreen'),
  'ResetPasswordScreen',
);
const LegalDocumentPage = lazyRouteComponent(
  () => import('@/features/legal/LegalDocumentPage'),
  'LegalDocumentPage',
);
const LegalAcceptPage = lazyRouteComponent(
  () => import('@/features/legal/LegalAcceptPage'),
  'LegalAcceptPage',
);
const HistoryTab = lazyRouteComponent(() => import('@/features/history/HistoryTab'), 'HistoryTab');
const ProfileTab = lazyRouteComponent(() => import('@/features/profile/ProfileTab'), 'ProfileTab');
const ChangeEmailPage = lazyRouteComponent(
  () => import('@/features/profile/ChangeEmailPage'),
  'ChangeEmailPage',
);
const ChangePasswordPage = lazyRouteComponent(
  () => import('@/features/profile/ChangePasswordPage'),
  'ChangePasswordPage',
);
const DeleteAccountPage = lazyRouteComponent(
  () => import('@/features/profile/DeleteAccountPage'),
  'DeleteAccountPage',
);
const CarsListPage = lazyRouteComponent(
  () => import('@/features/cars/CarsListPage'),
  'CarsListPage',
);
const CarFormPage = lazyRouteComponent(() => import('@/features/cars/CarFormPage'), 'CarFormPage');
// Lazy like the rest, and it earns it more than most: this chunk pulls in the whole wizard, and
// the Yandex loader behind it, for a screen most sessions never open.
const OrderWizardScreen = lazyRouteComponent(
  () => import('@/features/order/OrderWizardScreen'),
  'OrderWizardScreen',
);
const CarPickerScreen = lazyRouteComponent(
  () => import('@/features/order/CarPickerScreen'),
  'CarPickerScreen',
);
const OrderTrackingScreen = lazyRouteComponent(
  () => import('@/features/tracking/OrderTrackingScreen'),
  'OrderTrackingScreen',
);
const AddCarScreen = lazyRouteComponent(
  () => import('@/features/tracking/AddCarScreen'),
  'AddCarScreen',
);
const PasskeyPage = lazyRouteComponent(
  () => import('@/features/profile/PasskeyPage'),
  'PasskeyPage',
);
const ReceiptScreen = lazyRouteComponent(
  () => import('@/features/order/ReceiptScreen'),
  'ReceiptScreen',
);
const ChatScreen = lazyRouteComponent(() => import('@/features/chat/ChatScreen'), 'ChatScreen');
const PaymentMethodsPage = lazyRouteComponent(
  () => import('@/features/profile/PaymentMethodsPage'),
  'PaymentMethodsPage',
);
const NotificationSettingsPage = lazyRouteComponent(
  () => import('@/features/profile/NotificationSettingsPage'),
  'NotificationSettingsPage',
);
const AboutPage = lazyRouteComponent(() => import('@/features/profile/AboutPage'), 'AboutPage');
const FuelPricesPage = lazyRouteComponent(
  () => import('@/features/profile/FuelPricesPage'),
  'FuelPricesPage',
);
const ConsentsPage = lazyRouteComponent(
  () => import('@/features/profile/ConsentsPage'),
  'ConsentsPage',
);
const AddPaymentMethodPage = lazyRouteComponent(
  () => import('@/features/profile/AddPaymentMethodPage'),
  'AddPaymentMethodPage',
);

const PaymentMethodReturnPage = lazyRouteComponent(
  () => import('@/features/profile/PaymentMethodReturnPage'),
  'PaymentMethodReturnPage',
);

const ThreeDSecureReturnPage = lazyRouteComponent(
  () => import('@/features/order/ThreeDSecureReturnPage'),
  'ThreeDSecureReturnPage',
);
const SbpReturnPage = lazyRouteComponent(
  () => import('@/features/order/SbpReturnPage'),
  'SbpReturnPage',
);
const AddCarReturnPage = lazyRouteComponent(
  () => import('@/features/order/AddCarReturnPage'),
  'AddCarReturnPage',
);
const NotificationsPage = lazyRouteComponent(
  () => import('@/features/notifications/NotificationsPage'),
  'NotificationsPage',
);

/**
 * The route tree.
 *
 * Three structural decisions worth stating, because all three are easy to get backwards later:
 *
 * - **Tabs are routes, not state.** The mobile clients keep the selected tab in a variable; a
 *   PWA that did the same would break the browser's back button and make every screen
 *   unshareable. `/`, `/history` and `/profile` are real URLs under a shared layout that draws
 *   the tab bar; everything else is a pushed screen with its own back control.
 * - **The first-car gate is soft.** The mobile clients block the whole app behind «add a car»
 *   (`CarsGateViewModel`), which works when the only way in is the launcher. In a browser
 *   somebody arrives at an arbitrary URL, and a full-screen step with no exit is a trap. So the
 *   gate lives where it belongs — in front of the order wizard — and the home screen simply says
 *   «добавьте машину, чтобы заказать».
 * - **The legal gate, by contrast, is hard.** It is the one step that genuinely blocks the app,
 *   because it is the one step whose absence is a legal problem rather than a UX one: an account
 *   that has not accepted the current redaction may not order, and the server refuses it anyway.
 *   It lives in `protectedLayout.beforeLoad` (below) so no protected screen renders behind it and
 *   the browser's back button cannot slip past. Reading the documents stays public and unblocked.
 */

/**
 * Только свой путь: с одного `/`, но не с двух.
 *
 * `//evil.example` тоже начинается с `/`, а браузер читает это как адрес другого сайта. Сегодня
 * роутер сводит такой «путь» к `/` (проверено в router-core 1.171), но держаться на этом нельзя:
 * экран, через который человека проводят принудительно, — последнее место для open redirect.
 * То же правило в `features/legal/gate.ts`.
 */
export function isLocalPath(target: string): boolean {
  return target.startsWith('/') && !target.startsWith('//') && !target.startsWith('/\\');
}

export interface RouterContext {
  auth: AuthState;
  /**
   * The app's one `QueryClient`, so `beforeLoad` can ask the server a question before a screen
   * renders. Only the legal gate uses it — a guard that has to know something the profile does
   * not carry, and that must not be answered from a component after the screen is already up.
   */
  queryClient: QueryClient;
}

/**
 * The update toast lives here rather than in `AppShell` so it reaches pushed screens too — a
 * deploy does not wait for somebody to navigate back to a tab root. It renders nothing until a
 * newer worker is actually waiting, and suppresses itself where a reload would cost work.
 */
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <Outlet />
      <UpdateToast />
    </>
  ),
});

/**
 * Anything under here requires a session; anonymous visitors bounce to /login.
 *
 * And a session is not enough on its own: an account that has not accepted the current legal
 * redaction is stopped here too, on every navigation. That placement is what makes the gate a
 * gate — the browser's back button re-runs `beforeLoad`, so there is no history entry to slip
 * back through, and no screen renders behind the acceptance step to be seen or interacted with.
 *
 * The one question this guard has to ask over the network is asked of the server every time
 * (`pending`, cached for half a minute like any other answer, never persisted). A local «уже
 * принял» flag would be both forgeable and unprovable, so the client does not keep one.
 */
const protectedLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: async ({ context, location }) => {
    if (context.auth.status !== 'authenticated') {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }

    // The acceptance screen itself, or the guard would redirect it to itself forever.
    if (isGateExempt(location.pathname)) return;

    /**
     * A failed check lets the navigation through, deliberately. The server is the enforcement —
     * every endpoint that matters answers `legal_acceptance_required` and the transport turns
     * that into this same screen — so a dead connection costs at most an order that gets refused,
     * while the opposite choice locks the whole app behind a request that is not answering.
     */
    const pending = await context.queryClient
      .ensureQueryData(pendingLegalQueryOptions())
      .catch(() => undefined);

    if (needsAcceptance(pending)) {
      throw redirect({ to: LEGAL_ACCEPT_PATH, search: { redirect: location.href } });
    }
  },
  /**
   * Everything authenticated, plus the one thing that has to outlive a navigation.
   *
   * The paid toast is raised by the wizard in the same frame the wizard is replaced by the
   * tracking screen. Hosting it here — above the route that changes — is what lets it survive
   * that swap, and is the same placement both mobile clients use for the same reason.
   */
  component: () => (
    <>
      <Outlet />
      <PaidToast />
    </>
  ),
});

/** The three-tab shell. Only the tab roots live here — pushed screens must not keep the bar. */
const tabsLayout = createRoute({
  getParentRoute: () => protectedLayout,
  id: 'tabs',
  component: AppShell,
});

const homeRoute = createRoute({
  getParentRoute: () => tabsLayout,
  path: '/',
  component: HomeTab,
});

const historyRoute = createRoute({
  getParentRoute: () => tabsLayout,
  path: '/history',
  component: HistoryTab,
});

const profileRoute = createRoute({
  getParentRoute: () => tabsLayout,
  path: '/profile',
  component: ProfileTab,
});

const carsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/cars',
  component: CarsListPage,
});

/**
 * `returnTo` names the screen that sent us to a car form, so «добавить машину» from inside the
 * order flow comes back to the picker instead of dumping somebody in the garage mid-order.
 *
 * A closed set rather than a free path: an arbitrary string in a URL is an open redirect waiting
 * to happen, and there are exactly three places that need this.
 */
export type CarFormReturnTo = 'picker' | 'picker-add' | 'picker-swap';

function parseCarFormReturnTo(value: unknown): CarFormReturnTo | undefined {
  return value === 'picker' || value === 'picker-add' || value === 'picker-swap'
    ? value
    : undefined;
}

/**
 * `?replace=<id>` carries the «replace my only car» flow: the new car is created first and the
 * old one deleted afterwards, because deleting first is refused by the server (409
 * `last_car_undeletable`) and would strand the account at zero cars.
 */
const carCreateRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/cars/new',
  validateSearch: (
    search: Record<string, unknown>,
  ): { replace?: string; returnTo?: CarFormReturnTo } => {
    const replace = search['replace'];
    const returnTo = parseCarFormReturnTo(search['returnTo']);
    return {
      ...(typeof replace === 'string' && replace.length > 0 ? { replace } : {}),
      ...(returnTo !== undefined ? { returnTo } : {}),
    };
  },
  component: () => <CarFormPage mode="create" />,
});

const carEditRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/cars/$carId',
  component: () => <CarFormPage mode="edit" />,
});

/**
 * The first car, reached from the home screen's CTA and from the order wizard's gate. Same form
 * as `/cars/new` with a different title, an explanatory banner and no way to dismiss it — but
 * reachable only on purpose, never as a wall thrown up in front of the app.
 *
 * `returnTo` тот же, что у `/cars/new`, и он здесь не украшение: с главной сюда приходят, нажав
 * «Заказать заправку» при пустом гараже. Без него человек, добавивший первую машину, оказывался
 * снова на главной и начинал заказ заново — намерение, с которым он шёл, терялось на вставленном
 * посередине шаге. На Android этот путь всегда возвращал в шлюз заказа.
 */
const firstCarRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/first-car',
  validateSearch: (search: Record<string, unknown>): { returnTo?: CarFormReturnTo } => {
    const returnTo = parseCarFormReturnTo(search['returnTo']);
    return returnTo === undefined ? {} : { returnTo };
  },
  component: () => <CarFormPage mode="first" />,
});

/**
 * The order wizard — one route for all six steps.
 *
 * The step lives in the wizard's own state, not in the URL. Six routes would remount the map on
 * every «Далее» and «Назад», losing the camera and re-running geolocation each time; the wizard
 * intercepts Back itself and walks the step machine instead (see `OrderWizardScreen`).
 *
 * Outside the tab shell, like the car form: a bottom bar under a wizard competes with its own
 * «Далее», and there is nowhere to tab away to mid-order anyway.
 */
const orderWizardRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/order/new',
  component: OrderWizardScreen,
});

/**
 * Which car — C2, a destination of its own rather than a seventh wizard step.
 *
 * Two flags switch what picking a car means. `?add=1` appends to the выезд already being built,
 * which is where the fuel step's «заправить ещё одну» goes; `?swap=1` replaces the primary car
 * mid-flow, which is its «Сменить». Neither present means starting a new order.
 */
const orderCarRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/order/car',
  validateSearch: (search: Record<string, unknown>): { add?: boolean; swap?: boolean } => {
    const flag = (key: string) => search[key] === true || search[key] === '1';
    // Mutually exclusive, and `add` wins: a URL claiming both is malformed, and appending a car is
    // the reversible one of the two.
    if (flag('add')) return { add: true };
    if (flag('swap')) return { swap: true };
    return {};
  },
  component: CarPickerScreen,
});

/**
 * One order, live — D1.
 *
 * Placed after `/order/new` and `/order/car` in the tree so those literal paths are matched before
 * this parameterised one; «new» is not an order id.
 */
const orderTrackingRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/order/$orderId',
  component: OrderTrackingScreen,
});

/** Adding a car to a выезд already on the road — cheaper than a second order, and time-limited. */
const orderAddCarRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/order/$orderId/add-car',
  component: AddCarScreen,
});

/**
 * The receipt for one order — its own URL, deliberately.
 *
 * A receipt is a document somebody comes back to, links to and screenshots; folding it into the
 * tracking screen would leave it with no address of its own. It is reachable only from a billed
 * order, but the route does not enforce that — an order without a total simply renders dashes,
 * which is a truthful answer to a URL somebody typed.
 */
const orderReceiptRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/order/$orderId/receipt',
  component: ReceiptScreen,
});

/**
 * Support chat — one thread per account, not per order.
 *
 * A top-level route rather than a child of the order: that is the backend's shape, and nesting it
 * under an order would imply a per-order conversation the operator does not see.
 */
const chatRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/chat',
  component: ChatScreen,
});

const paymentMethodsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/profile/payment-methods',
  component: PaymentMethodsPage,
});

/**
 * Notification settings.
 *
 * Under `/profile`, not `/notifications` — that path is the notification *centre*, the history of
 * what was sent. Two different screens whose names collide in Russian, which is exactly why the
 * URLs must not.
 */
/**
 * Where the bank returns the payer after its card form.
 *
 * The session id is the ONLY thing read out of the URL, and it is validated as a plain non-empty
 * string — the outcome itself is re-read server-side, so nothing here decides whether a card was
 * linked. A missing or malformed `session` renders the screen's "ссылка неполная" state rather than
 * throwing, because this URL is typed by a bank, not by us.
 */
/**
 * Where the payment gateway returns the payer after a 3-D Secure challenge on an ORDER.
 *
 * No `validateSearch`: the gateway appends its own parameters and none of them are trusted — the
 * screen re-sends the order request and lets the server say what happened.
 */
const threeDSecureReturnRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/order/3ds-return',
  component: ThreeDSecureReturnPage,
});

/**
 * Куда банк возвращает плательщика после оплаты по СБП.
 *
 * Отдельный адрес от карточного, и идентификатор платежа стоит в ПУТИ: шлюз дописывает к адресу
 * возврата свои параметры, а путь он не трогает. Экран оформляет заявку на этот платёж — тогда
 * как карточная страница возврата повторяет создание заказа КАРТОЙ и на заказе по СБП упирается
 * в «Выберите способ оплаты».
 */
const sbpReturnRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/order/sbp-return/$paymentId',
  component: SbpReturnPage,
});

/**
 * Куда банк возвращает плательщика после подтверждения ДОЗАКАЗА машины картой.
 *
 * Свой адрес: карточная страница возврата повторяет создание заказа из черновика, а здесь заказ
 * давно есть и черновика нет — повторять надо добавление в существующий выезд. Идентификатор
 * заказа — в ПУТИ: шлюз дописывает к адресу возврата свои параметры, а путь не трогает.
 */
const addCarReturnRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/order/add-car-return/$orderId',
  component: AddCarReturnPage,
});

const paymentMethodReturnRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/profile/payment-methods/return',
  validateSearch: (search: Record<string, unknown>): { session?: string } => {
    const session = search['session'];
    return typeof session === 'string' && session.length > 0 ? { session } : {};
  },
  component: PaymentMethodReturnPage,
});

/**
 * «Добавить карту».
 *
 * `returnTo=order` приносит сюда намерение, с которым человек пришёл: он не карту хотел завести,
 * он оформлял заказ и упёрся в то, что платить нечем. Привязка уводит на страницу банка и обратно
 * — двумя полными перезагрузками, — поэтому само намерение до возврата доживает не в адресе, а
 * рядом с черновиком заказа (см. `features/profile/cardReturn`). Здесь оно только объявляется.
 */
const addPaymentMethodRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/profile/payment-methods/new',
  validateSearch: (search: Record<string, unknown>): { returnTo?: 'order' } =>
    search['returnTo'] === 'order' ? { returnTo: 'order' } : {},
  component: AddPaymentMethodPage,
});

/** Face ID / Touch ID / Windows Hello — per device, which is why it has a screen and not a switch. */
const passkeyRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/profile/passkey',
  component: PasskeyPage,
});

const notificationSettingsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/profile/notifications',
  component: NotificationSettingsPage,
});

const aboutRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/profile/about',
  component: AboutPage,
});

/**
 * Журнал согласий этого аккаунта. Под `protectedLayout` — список принадлежит конкретному
 * человеку и отдаётся только по его токену; публичной страницы у него быть не может.
 */
const consentsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/profile/consents',
  component: ConsentsPage,
});

/**
 * «Цены на топливо» — прайс по городам. Под `protectedLayout` вместе с остальным профилем: сам
 * справочник цен не секрет, но читается он тем же токеном, что и всё остальное в приложении.
 */
const fuelPricesRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/profile/fuel-prices',
  component: FuelPricesPage,
});

/**
 * The mandatory acceptance step, built exactly like `firstCarRoute`: a full screen under the
 * protected layout, outside the tab shell, with no exit inside the page.
 *
 * Unlike the first-car step it is genuinely blocking — `protectedLayout.beforeLoad` above sends
 * every other protected URL here while anything is pending. `redirect` carries where the person
 * was going so the interrupted step is resumed rather than replaced by the home screen; it is
 * validated the same way `/login` validates its own, because a screen everybody is forced
 * through is the last place to accept an arbitrary URL.
 */
const legalAcceptRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: LEGAL_ACCEPT_PATH,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const target = search['redirect'];
    return typeof target === 'string' && isLocalPath(target) ? { redirect: target } : {};
  },
  component: LegalAcceptPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/notifications',
  component: NotificationsPage,
});

const changePasswordRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/profile/password',
  component: ChangePasswordPage,
});

/**
 * «Изменить почту» — она же «подтвердить свою». Ссылка из письма о смене адреса ведёт сюда, но
 * параметры её не нужны: экран за спиной у человека ничего не подтверждает, потому что смена
 * адреса требует действующей сессии, а письмо могли открыть где угодно.
 */
const changeEmailRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/profile/email',
  /**
   * `redirect` приносит сюда отказ `email_not_verified`, случившийся посреди оформления: там, куда
   * человека вернут, лежит черновик заказа. Проверяется так же, как у `/login` и гейта
   * документов, — экран, через который человека проводят принудительно, последнее место, где
   * можно принять произвольный URL.
   */
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const target = search['redirect'];
    return typeof target === 'string' && isLocalPath(target) ? { redirect: target } : {};
  },
  component: ChangeEmailPage,
});

const deleteAccountRoute = createRoute({
  getParentRoute: () => protectedLayout,
  path: '/profile/delete',
  component: DeleteAccountPage,
});

/** Public routes. A signed-in visitor is sent home rather than shown a second login form. */
const anonymousOnly = ({ context }: { context: RouterContext }) => {
  if (context.auth.status === 'authenticated') throw redirect({ to: '/' });
};

/**
 * Куда отправить того, кто на форме входа оказался уже войдя.
 *
 * Случай не выдуманный: ограниченное по времени восстановление сессии (`App`'s
 * `RESTORE_TIMEOUT_MS`) сдаётся и приводит человека сюда, а профиль приходит секундой позже. К
 * этому моменту в адресе уже лежит `redirect` — куда он шёл. Отправить его на главную значит
 * молча потерять открытый заказ.
 */
const backToWhereTheyWereHeaded = ({
  context,
  search,
}: {
  context: RouterContext;
  search: { redirect?: string };
}) => {
  if (context.auth.status !== 'authenticated') return;
  const target = search.redirect;
  if (target && isLocalPath(target)) throw redirect({ href: target });
  throw redirect({ to: '/' });
};

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const target = search['redirect'];
    return typeof target === 'string' && isLocalPath(target) ? { redirect: target } : {};
  },
  beforeLoad: backToWhereTheyWereHeaded,
  component: LoginScreen,
});

/**
 * Разбор `?email=…&code=…` у `/register` — параметров ссылки «подтвердить одной кнопкой» из
 * письма с кодом: человек читает почту на том же телефоне, жмёт ссылку и попадает сразу на шаг
 * подтверждения с уже подставленным кодом.
 *
 * Две вещи, которые здесь легко сделать неправильно.
 *
 * Первая: роутер разбирает значения как JSON, поэтому `code=482913` приезжает ЧИСЛОМ, а
 * `code=000042` — строкой. Без приведения к одному типу экран получал бы то одно, то другое, а
 * код с ведущим нулём вёл бы себя иначе, чем без него. Ведущие нули число к этому моменту уже
 * потеряло — их восстанавливает дополнение до шести знаков.
 *
 * Вторая: форма проверяется, а не только тип. Мусор в параметре иначе доехал бы до сервера и
 * потратил одну из пяти попыток на код, которого человек не вводил.
 *
 * Экспортируется ради собственного теста: поведение маршрута иначе проверяется только через
 * смонтированное приложение, а роутер там — синглтон, который держит разобранные параметры между
 * тестами.
 */
export function parseRegisterSearch(search: Record<string, unknown>): {
  email?: string;
  code?: string;
} {
  const email = search['email'];
  const rawCode = search['code'];
  const code =
    typeof rawCode === 'string'
      ? rawCode
      : typeof rawCode === 'number' && Number.isInteger(rawCode) && rawCode >= 0
        ? String(rawCode).padStart(6, '0')
        : undefined;
  return {
    ...(typeof email === 'string' && email.length > 0 ? { email } : {}),
    ...(code !== undefined && /^\d{6}$/.test(code) ? { code } : {}),
  };
}
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  validateSearch: parseRegisterSearch,
  beforeLoad: anonymousOnly,
  component: RegisterScreen,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordScreen,
});

/**
 * The other half of the reset flow — the half neither mobile client has. The email links here
 * (`CONSUMER_PASSWORD_RESET_URL_BASE?token=…`), so this route has to exist at exactly this path
 * on exactly this host, and it must survive a signed-in visitor too: a person who forgot their
 * password on their phone may well click the link on a laptop where the session is still alive.
 */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    const token = search['token'];
    return typeof token === 'string' && token.length > 0 ? { token } : {};
  },
  component: ResetPasswordScreen,
});

/**
 * The reader for the offer and the personal-data consent — `/legal/offer`, `/legal/privacy`,
 * `/legal/personal-data`.
 *
 * Deliberately a ROOT route and not a protected one: `GET /v1/legal/documents/{kind}` needs no
 * bearer, and the two places these links are followed from — the registration form and the
 * acceptance gate — are both places where the app is not yet usable. A document somebody is
 * about to accept has to be readable without first being let in.
 */
const legalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/$document',
  component: LegalDocumentPage,
});

const routeTree = rootRoute.addChildren([
  protectedLayout.addChildren([
    tabsLayout.addChildren([homeRoute, historyRoute, profileRoute]),
    carsRoute,
    carCreateRoute,
    carEditRoute,
    firstCarRoute,
    orderWizardRoute,
    orderCarRoute,
    orderTrackingRoute,
    orderAddCarRoute,
    orderReceiptRoute,
    chatRoute,
    paymentMethodsRoute,
    addPaymentMethodRoute,
    paymentMethodReturnRoute,
    threeDSecureReturnRoute,
    sbpReturnRoute,
    addCarReturnRoute,
    notificationSettingsRoute,
    passkeyRoute,
    aboutRoute,
    consentsRoute,
    fuelPricesRoute,
    legalAcceptRoute,
    notificationsRoute,
    changePasswordRoute,
    changeEmailRoute,
    deleteAccountRoute,
  ]),
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  legalRoute,
]);

export const router = createRouter({
  routeTree,
  // Filled in by RouterProvider — the auth object only exists inside the React tree.
  context: undefined as unknown as RouterContext,
  // Start fetching a screen's chunk when a link is hovered or touched, so the split is invisible
  // in normal use and only costs anything on a cold jump straight to a URL.
  defaultPreload: 'intent',
  defaultErrorComponent: RouteError,
  // Без него неизвестный адрес рисовал пустую страницу: у корневого маршрута есть `Outlet`, а
  // подставлять в него нечего. Опечатка в адресе и старая ссылка — единственные два способа
  // сюда попасть, и оба заканчиваются экраном с выходом, а не пустотой.
  defaultNotFoundComponent: NotFound,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
